<template>
  <Sidebar
    v-model:visible="visible"
    class="p-sidebar-lg modern-sidebar"
    position="right"
    :showCloseIcon="false"
  >
    <Loading
      v-if="updating"
      :title="'Updating mediaspace ad...'"
      :full="false"
    />
    <div v-show="!updating" class="mediaspace-form-container">
      <div class="flex align-items-center mb-4">
        <h3 class="text-xl font-semibold m-0">✨ Update mediaspace ad</h3>
      </div>
      <Form
        :fields="updateFields"
        :context="updateEntity"
        @onChange="updateEntityData"
        @onAction="onUpdate"
        :actionLabel="'Update ad'"
        :actionIcon="'pi pi-plus-circle'"
        class="mediaspace-form"
      />
    </div>
  </Sidebar>
</template>

<script>
import { ref, watch } from "vue";
import { useToast } from "primevue/usetoast";

export default {
  components: {},
  emits: ["update:isVisible", "onUpdate"],
  props: ["isVisible", "updating", "entity"],
  setup(props, { emit }) {
    const visible = ref(false);
    const toast = useToast();

    const targetTypes = [
      { title: "All Users", id: "All" },
      { title: "Free Only", id: "FreeOnly" },
      { title: "Subscribers Only", id: "SubscribersOnly" },
    ];

    const locationOptions = [
      { title: "Home", id: "Home" },
      { title: "Benefits", id: "Benefits" },
      { title: "Explore", id: "Explore" },
    ];

    const updateEntity = ref({});

    const updateFields = [
      {
        id: "backofficeTitle",
        type: "text-input",
        title: "📝 Backoffice Title",
        help: "Title for internal management",
        config: {
          placeholder: "Enter backoffice title",
        },
      },
      {
        id: "index",
        type: "number-input",
        title: "🔢 Display Order",
        help: "Order in which this ad should appear (lower numbers appear first)",
        config: {
          min: 1,
          step: 1,
        },
      },
      {
        id: "displayTimeSeconds",
        type: "number-input",
        title: "⏱️ Display Time (seconds)",
        help: "How long this ad should be displayed before moving to the next one",
        config: {
          min: 1,
          max: 60,
          step: 1,
        },
      },
      {
        id: "targetUrl",
        type: "localized-text",
        title: "🔗 Target URL",
        help: "URL where users will be redirected when clicking the ad",
      },
      {
        id: "isScratcherWithAutoRegistration",
        type: "switch",
        title: "🎰 Is Scratcher game with auto user registration",
        help: "Remember to enable auto user registration in Scratcher",
      },
      {
        id: "scratcherRecurrenceText",
        type: "localized-text",
        title: "🔄 Scratcher Recurrence",
        help: "How often the user can play the game. Shown on unsuccessful retry.",
        condition: (context) => context.isScratcherWithAutoRegistration === true,
      },
      {
        id: "coverImageUrl",
        type: "imageUpload",
        title: "🎬 Cover Video",

        accept: "video/mp4",

        help: "Video to display for the ad (MP4 format)",
      },
      {
        id: "locations",
        type: "multi",
        title: "📍 Locations",
        help: "Where this ad should be displayed",
        config: {
          options: locationOptions,
          optionLabel: "title",
          optionValue: "id",
          placeholder: "Select locations",
        },
      },
      {
        id: "targetType",
        type: "dropdown",
        title: "🎯 Target Type",
        help: "Which users should see this ad",
        config: {
          options: targetTypes,
          optionLabel: "title",
          optionValue: "id",
          placeholder: "Please select target type",
        },
      },
      {
        id: "targetMinAge",
        type: "number-input",
        title: "👶 Minimum Age",
        help: "Minimum age for users to see this ad",
        config: {
          min: 0,
          max: 100,
        },
      },
      {
        id: "targetMaxAge",
        type: "number-input",
        title: "👴 Maximum Age",
        help: "Maximum age for users to see this ad",
        config: {
          min: 0,
          max: 100,
        },
      },
      {
        id: "audiencePreview",
        type: "AudiencePreview",
        title: "",
        help: "",
      },
      {
        id: "targetLocation",
        type: "GoogleMapsAddress",
        title: "📍 Target Location",
        help: "Optional specific location for geographic targeting",
      },
      {
        id: "targetRadius",
        type: "number-input",
        title: "📏 Target Radius (km)",
        help: "Radius in kilometers for geographic targeting (0 = no targeting)",
        config: {
          min: 0,
          step: 0.1,
        },
      },
      {
        id: "enabled",
        type: "switch",
        title: "🔌 Enabled",
        help: "Whether this ad is active",
      },
    ];

    const updateEntityData = () => {
      // Handle any entity updates if needed
    };

    const onUpdate = (formContext) => {
      // Validate required fields
      if (
        !formContext.backofficeTitle ||
        formContext.backofficeTitle.trim() === ""
      ) {
        toast.add({
          severity: "error",
          summary: "Validation Error",
          detail: "Backoffice title is required.",
          life: 5000,
        });
        return;
      }

      // Validate LocalizedText fields
      const targetUrlEn = formContext.targetUrl?.translations?.find(
        (t) => t.languageCode === "en"
      )?.text;
      if (!targetUrlEn || targetUrlEn.trim() === "") {
        toast.add({
          severity: "error",
          summary: "Validation Error",
          detail: "Target URL (English) is required.",
          life: 5000,
        });
        return;
      }

      if (!formContext.coverImageUrl) {
        toast.add({
          severity: "error",
          summary: "Validation Error",
          detail: "Cover video is required.",
          life: 5000,
        });
        return;
      }

      if (!formContext.locations || formContext.locations.length === 0) {
        toast.add({
          severity: "error",
          summary: "Validation Error",
          detail: "At least one location must be selected.",
          life: 5000,
        });
        return;
      }

      // Convert empty location object to null
      if (
        formContext.targetLocation &&
        (!formContext.targetLocation.street ||
          formContext.targetLocation.street.trim() === "")
      ) {
        console.log("Converting empty target location to null");
        formContext.targetLocation = null;
      }

      emit("onUpdate", formContext);
    };

    // Watch for changes from parent
    watch(
      () => props.isVisible,
      (newVal) => {
        visible.value = newVal;
      }
    );

    watch(
      () => visible.value,
      (newVal) => {
        emit("update:isVisible", newVal);
      }
    );

    // Watch for entity changes and populate form
    watch(
      () => props.entity,
      (newVal) => {
        if (newVal) {
          // Convert LocalizedText format from API to form format
          const targetUrl = newVal.targetUrl || {
            translations: [
              { languageCode: "en", text: "" },
              { languageCode: "da", text: "" },
              { languageCode: "fi", text: "" },
              { languageCode: "no", text: "" },
            ],
          };

          const scratcherRecurrenceText = newVal.scratcherRecurrenceText || {
            translations: [
              { languageCode: "en", text: "" },
              { languageCode: "da", text: "" },
              { languageCode: "fi", text: "" },
              { languageCode: "no", text: "" },
            ],
          };

          // Convert null location to empty object structure for GoogleMapsAddress component
          const targetLocation = newVal.targetLocation || {
            street: "",
            postalCode: "",
            city: "",
            country: "",
            latitude: null,
            longitude: null,
          };

          updateEntity.value = {
            ...newVal,
            targetUrl,
            scratcherRecurrenceText,
            targetLocation,
          };
        }
      },
      { immediate: true }
    );

    return {
      visible,
      updateFields,
      updateEntity,
      updateEntityData,
      onUpdate,
    };
  },
};
</script>

<style scoped>
/* Modern sidebar styling */
:deep(.modern-sidebar) {
  background: white;
  border-left: 1px solid #e5e7eb;
}

:deep(.modern-sidebar .p-sidebar-header) {
  background: white;
  border-bottom: 1px solid #e5e7eb;
  padding: 1.5rem;
}

:deep(.modern-sidebar .p-sidebar-content) {
  padding: 0;
  background: white;
}

.mediaspace-form-container {
  padding: 1.5rem;
  height: 100%;
  overflow-y: auto;
}

.mediaspace-form {
  height: 100%;
}

/* Form styling */
:deep(.form-field) {
  margin-bottom: 1.5rem;
}

:deep(.form-field label) {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  display: block;
}

:deep(.form-field input),
:deep(.form-field select),
:deep(.form-field textarea) {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  transition: border-color 0.2s ease;
}

:deep(.form-field input:focus),
:deep(.form-field select:focus),
:deep(.form-field textarea:focus) {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Switch styling */
:deep(.p-inputswitch) {
  margin-top: 0.5rem;
}

/* Multi-select styling */
:deep(.p-multiselect) {
  width: 100%;
}

/* Image upload styling */
:deep(.image-upload-container) {
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  transition: border-color 0.2s ease;
}

:deep(.image-upload-container:hover) {
  border-color: #3b82f6;
}

/* Help text styling */
:deep(.form-help) {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
}

/* Action button styling */
:deep(.form-action-button) {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%) !important;
  border: none !important;
  padding: 0.75rem 1.5rem !important;
  border-radius: 0.75rem !important;
  font-weight: 600 !important;
  color: white !important;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
  transition: all 0.3s ease !important;
  width: auto !important;
  margin-top: 1rem;
  min-width: 150px;
  flex: none !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
}

:deep(.form-action-button:hover) {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 15px -3px rgba(0, 0, 0, 0.2) !important;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .mediaspace-form-container {
    padding: 1rem;
  }

  :deep(.form-field) {
    margin-bottom: 1rem;
  }
}
</style> 